import { Schema } from "effect";
import { ProjectIdSchema, RequestIdSchema, UserIdSchema, WorkspaceIdSchema } from "./ids";
import { NonNegativeSafeIntegerSchema, PapersFlowBoundaryParseOptions } from "./standard";
import {
  ProjectDescriptionSchema,
  ProjectInstructionsSchema,
  ProjectNameSchema,
} from "./workspace";

export const LocalProjectIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(160),
  Schema.makeFilter((value) => /^[A-Za-z0-9_-]+$/u.test(value), {
    expected: "an opaque local project identifier",
  }),
).pipe(Schema.brand("LocalProjectId"));

export const CloudProjectRefSchema = Schema.Struct({
  kind: Schema.Literal("cloud"),
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
});

export const LocalProjectRefSchema = Schema.Struct({
  kind: Schema.Literal("local"),
  localProjectId: LocalProjectIdSchema,
});

export const ProjectRefSchema = Schema.Union([CloudProjectRefSchema, LocalProjectRefSchema]);
export const ProjectStorageKindSchema = Schema.Literals(["cloud", "local"]);
export const ProjectAccessSchema = Schema.Literals(["owner", "member", "read-only"]);
export const ProjectVisibilitySchema = Schema.Literals(["restricted", "workspace"]);
export const ProjectMemberRoleSchema = Schema.Literals(["viewer", "collaborator", "admin"]);

export const ProjectAvailabilitySchema = Schema.Union([
  Schema.Struct({ state: Schema.Literal("ready") }),
  Schema.Struct({ state: Schema.Literal("offline"), reason: Schema.Literal("network") }),
  Schema.Struct({
    state: Schema.Literal("unavailable"),
    reason: Schema.Literals([
      "missing-root",
      "permission-required",
      "unsupported-version",
      "account-required",
    ]),
  }),
]);

export const ProjectCapabilityReasonSchema = Schema.Literals([
  "read-only",
  "requires-auth",
  "requires-network",
  "quota-blocked",
  "platform-unavailable",
  "not-yet-available",
  "project-archived",
  "policy-denied",
]);

export const ProjectCapabilityStateSchema = Schema.Union([
  Schema.Struct({ state: Schema.Literal("enabled") }),
  Schema.Struct({
    state: Schema.Literal("disabled"),
    reason: ProjectCapabilityReasonSchema,
  }),
]);

export const ProjectCapabilitiesSchema = Schema.Struct({
  files: ProjectCapabilityStateSchema,
  ai: ProjectCapabilityStateSchema,
  compile: ProjectCapabilityStateSchema,
  publish: ProjectCapabilityStateSchema,
  share: ProjectCapabilityStateSchema,
  realtimeCollaboration: ProjectCapabilityStateSchema,
  localShell: ProjectCapabilityStateSchema,
});

export const ProjectCatalogItemSchema = Schema.Struct({
  ref: ProjectRefSchema,
  title: ProjectNameSchema,
  storageKind: ProjectStorageKindSchema,
  access: ProjectAccessSchema,
  availability: ProjectAvailabilitySchema,
  updatedAt: NonNegativeSafeIntegerSchema,
  capabilities: ProjectCapabilitiesSchema,
});

export const ProjectInitialMemberSchema = Schema.Struct({
  userId: UserIdSchema,
  role: ProjectMemberRoleSchema,
});

export const CreateCloudProjectRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  name: ProjectNameSchema,
  description: Schema.optionalKey(ProjectDescriptionSchema),
  instructions: Schema.optionalKey(ProjectInstructionsSchema),
  visibility: ProjectVisibilitySchema,
  initialMembers: Schema.Array(ProjectInitialMemberSchema).check(Schema.isMaxLength(250)),
});

export const CreateCloudProjectResultSchema = Schema.Struct({
  ref: CloudProjectRefSchema,
  replayed: Schema.Boolean,
});

export const UpdateCloudProjectRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  name: ProjectNameSchema,
  description: Schema.optionalKey(ProjectDescriptionSchema),
  instructions: Schema.optionalKey(ProjectInstructionsSchema),
});

export const SetProjectVisibilityRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  visibility: ProjectVisibilitySchema,
});

export const SetProjectMemberRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  userId: UserIdSchema,
  role: ProjectMemberRoleSchema,
});

export const RemoveProjectMemberRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  userId: UserIdSchema,
});

export const decodeProjectRef = Schema.decodeUnknownSync(
  ProjectRefSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProjectCatalogItem = Schema.decodeUnknownSync(
  ProjectCatalogItemSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCreateCloudProjectRequest = Schema.decodeUnknownSync(
  CreateCloudProjectRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeUpdateCloudProjectRequest = Schema.decodeUnknownSync(
  UpdateCloudProjectRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeSetProjectVisibilityRequest = Schema.decodeUnknownSync(
  SetProjectVisibilityRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeSetProjectMemberRequest = Schema.decodeUnknownSync(
  SetProjectMemberRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeRemoveProjectMemberRequest = Schema.decodeUnknownSync(
  RemoveProjectMemberRequestSchema,
  PapersFlowBoundaryParseOptions,
);

export type LocalProjectId = typeof LocalProjectIdSchema.Type;
export type CloudProjectRef = typeof CloudProjectRefSchema.Type;
export type CloudProjectRefEncoded = typeof CloudProjectRefSchema.Encoded;
export type LocalProjectRef = typeof LocalProjectRefSchema.Type;
export type LocalProjectRefEncoded = typeof LocalProjectRefSchema.Encoded;
export type ProjectRef = typeof ProjectRefSchema.Type;
export type ProjectRefEncoded = typeof ProjectRefSchema.Encoded;
export type ProjectStorageKind = typeof ProjectStorageKindSchema.Type;
export type ProjectAccess = typeof ProjectAccessSchema.Type;
export type ProjectVisibility = typeof ProjectVisibilitySchema.Type;
export type ProjectMemberRole = typeof ProjectMemberRoleSchema.Type;
export type ProjectAvailability = typeof ProjectAvailabilitySchema.Type;
export type ProjectCapabilityReason = typeof ProjectCapabilityReasonSchema.Type;
export type ProjectCapabilityState = typeof ProjectCapabilityStateSchema.Type;
export type ProjectCapabilities = typeof ProjectCapabilitiesSchema.Type;
export type ProjectCatalogItem = typeof ProjectCatalogItemSchema.Type;
export type ProjectCatalogItemEncoded = typeof ProjectCatalogItemSchema.Encoded;
export type CreateCloudProjectRequest = typeof CreateCloudProjectRequestSchema.Type;
export type CreateCloudProjectRequestEncoded = typeof CreateCloudProjectRequestSchema.Encoded;
export type CreateCloudProjectResult = typeof CreateCloudProjectResultSchema.Type;
export type CreateCloudProjectResultEncoded = typeof CreateCloudProjectResultSchema.Encoded;
export type UpdateCloudProjectRequest = typeof UpdateCloudProjectRequestSchema.Type;
export type SetProjectVisibilityRequest = typeof SetProjectVisibilityRequestSchema.Type;
export type SetProjectMemberRequest = typeof SetProjectMemberRequestSchema.Type;
export type RemoveProjectMemberRequest = typeof RemoveProjectMemberRequestSchema.Type;
